// src/components/tabs/OverviewTab.tsx

import React from "react";
import {
  Card,
  Statistic,
  Divider,
  Tooltip,
  Row,
  Col,
  Button,
  Space,
  Tag,
  Typography,
  Popconfirm,
  message,
  Switch,
} from "antd";
import {
  InfoCircleOutlined,
  ContainerOutlined,
  ClearOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { StorageStats, SyncQueueStatus } from "../types/storage.types";
import { useRouterStore } from "../../../../../stores";

const { Text } = Typography;

interface OverviewTabProps {
  stats: StorageStats;
  syncQueue: SyncQueueStatus;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  stats,
  syncQueue,
}) => {
  const byPassSw = useRouterStore(state => state.byPassSw);
  const setByPassSw = useRouterStore(state => state.setByPassSw);
  const isContainerProcessedToday = useRouterStore(state => state.isContainerProcessedToday);
  const currentDateKey = useRouterStore(state => state.currentDateKey);
  const processedContainersToday = useRouterStore(state => state.processedContainersToday);



  // ✅ NUEVO: Función para limpiar contenedores manualmente
  const handleClearContainers = () => {
    // Usar addProcessedContainer para forzar reset (trick)
    useRouterStore.setState({
      processedContainersToday: new Set(),
      currentDateKey: new Date().toDateString(),
    });
    message.success("Contenedores procesados limpiados correctamente");
  };

  // ✅ NUEVO: Procesar la data de contenedores para mostrar
  const containerStats = React.useMemo(() => {
    const containers = Array.from(processedContainersToday);
    const emptyContainers = containers.filter((c) => c.endsWith("-empty"));
    const fullContainers = containers.filter((c) => c.endsWith("-full"));

    // Extraer números únicos de contenedores
    const uniqueContainers = new Set(
      containers.map((c) => c.replace("-empty", "").replace("-full", ""))
    );

    return {
      total: containers.length,
      unique: uniqueContainers.size,
      empty: emptyContainers.length,
      full: fullContainers.length,
      containers: Array.from(uniqueContainers),
    };
  }, [processedContainersToday]);

  return (
    <>
      {/* Estadísticas principales */}
      <Card title="Estadísticas Generales" style={{ marginBottom: 10 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic title="Total" value={stats.totalItems} />
          </Col>
          <Col span={6}>
            <Statistic
              title="Fotos"
              value={stats.photoItems}
              // prefix={<PictureOutlined />}
            />
          </Col>
          <Col span={7}>
            <Statistic
              title="Procesos"
              value={stats.workflowItems}
              // prefix={<FileTextOutlined />}
            />
          </Col>
          <Col span={5}>
            <Statistic
              title="Sync"
              value={stats.syncQueueItems}
              valueStyle={{
                color: stats.syncQueueItems > 0 ? "#faad14" : "#52c41a",
              }}
            />
          </Col>
        </Row>
      </Card>

      {/* ✅ NUEVO: Sección de contenedores procesados hoy */}
      <Card
        title={
          <Space>
            <ContainerOutlined />
            <p style={{ fontSize: "0.8rem" }}>Contenedores Procesados Hoy</p>
            <Tag color="blue">{currentDateKey}</Tag>
          </Space>
        }
        style={{ marginBottom: 16 }}
        extra={
          <Popconfirm
            title="¿Limpiar contenedores procesados?"
            description="Esto permitirá volver a procesar contenedores del día"
            onConfirm={handleClearContainers}
            okText="Sí, limpiar"
            cancelText="Cancelar"
          >
            <Button
              size="small"
              icon={<ClearOutlined />}
              danger
              disabled={containerStats.total === 0}
            />
          </Popconfirm>
        }
      >
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="Total"
              value={containerStats.total}
              prefix={<CalendarOutlined />}
              valueStyle={{
                color: containerStats.total > 0 ? "#52c41a" : "#666",
              }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Únicos"
              value={containerStats.unique}
              valueStyle={{
                color: containerStats.unique > 0 ? "#1890ff" : "#666",
              }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Vacíos"
              value={containerStats.empty}
              valueStyle={{
                color: containerStats.empty > 0 ? "#faad14" : "#666",
              }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Llenos"
              value={containerStats.full}
              valueStyle={{
                color: containerStats.full > 0 ? "#52c41a" : "#666",
              }}
            />
          </Col>
        </Row>

        {/* Lista de contenedores procesados */}
        {containerStats.containers.length > 0 && (
          <>
            <Divider />
            <div style={{ marginBottom: 8 }}>
              <Text strong>Contenedores procesados:</Text>
            </div>
            <Space wrap>
              {containerStats.containers.map((container) => {
                const hasEmpty = isContainerProcessedToday(container, "empty");
                const hasFull = isContainerProcessedToday(container, "full");

                let color = "#666";
                let status = "";

                if (hasEmpty && hasFull) {
                  color = "#52c41a";
                  status = "✅ Completo";
                } else if (hasEmpty) {
                  color = "#faad14";
                  status = "📦 Solo Vacío";
                } else if (hasFull) {
                  color = "#1890ff";
                  status = "📦 Solo Lleno";
                }

                return (
                  <Tooltip key={container} title={status}>
                    <Tag
                      color={
                        color === "#52c41a"
                          ? "green"
                          : color === "#faad14"
                          ? "orange"
                          : "blue"
                      }
                    >
                      {container}
                      {hasEmpty && hasFull ? " ✅" : hasEmpty ? " 📦" : " 📦"}
                    </Tag>
                  </Tooltip>
                );
              })}
            </Space>
          </>
        )}

        {containerStats.total === 0 && (
          <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
            <ContainerOutlined
              style={{ fontSize: "24px", marginBottom: "8px" }}
            />
            <div>No se han procesado contenedores hoy</div>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Los contenedores aparecerán aquí cuando se registren
            </Text>
          </div>
        )}
      </Card>

      {/* Estado de sincronización */}
      <Card title="Estado de Sincronización" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Statistic
              title="Pendientes"
              value={syncQueue.pendingPosts}
              valueStyle={{
                color: syncQueue.pendingPosts > 0 ? "#faad14" : "#52c41a",
              }}
            />
          </Col>
          <Col span={12}>
            <Statistic
              title="Última"
              value={
                syncQueue.lastSyncStatus
                  ? syncQueue.lastSyncStatus === "success"
                    ? "Exitosa"
                    : "Fallida"
                  : "No iniciada"
              }
              valueStyle={{
                color:
                  syncQueue.lastSyncStatus === "success"
                    ? "#52c41a"
                    : syncQueue.lastSyncStatus === "failed"
                    ? "#ff4d4f"
                    : "#666",
              }}
            />
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col style={{ minWidth: "100%" }}>
            <Space style={{ minWidth: "100%", padding:"0px", margin:"0px" }}>
              <InfoCircleOutlined style={{ color: "red" }} />
              <strong style={{ fontSize: "1rem", color: "red" }}>
                Bypass SW
              </strong>
              <Switch
                checkedChildren="ON"
                unCheckedChildren="OFF"
                size="default"
                checked={byPassSw}
                onChange={(checked) => {
                  setByPassSw(checked);
                }}
              />
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Información técnica */}
      <Card title="Información Técnica" type="inner">
        <Row gutter={16}>
          <Col span={12}>
            <div>
              <strong>Elemento más antiguo:</strong>
            </div>
            <div style={{ color: "#666", fontSize: "12px" }}>
              {stats.oldestTimestamp
                ? stats.oldestTimestamp.toLocaleString()
                : "N/A"}
            </div>
          </Col>
          <Col span={12}>
            <div>
              <strong>Elemento más reciente:</strong>
            </div>
            <div style={{ color: "#666", fontSize: "12px" }}>
              {stats.newestTimestamp
                ? stats.newestTimestamp.toLocaleString()
                : "N/A"}
            </div>
          </Col>
        </Row>

        {/* Distribución por stores */}
        {Object.keys(stats.byStore).length > 0 && (
          <>
            <Divider />
            <div style={{ marginBottom: 16 }}>
              <strong>Distribución por Stores</strong>
            </div>
            <Row gutter={16}>
              {Object.entries(stats.byStore).map(([storeName, count]) => (
                <Col span={8} key={storeName}>
                  <Statistic
                    title={storeName.replace("-", " ").toUpperCase()}
                    value={count}
                    valueStyle={{
                      color: count > 0 ? "#1890ff" : "#666",
                      fontSize: "16px",
                    }}
                  />
                </Col>
              ))}
            </Row>
          </>
        )}
      </Card>
    </>
  );
};
